"use client";

// The ⌘K palette. Wraps cmdk's Command.Dialog and:
//
//   1. Globally listens for ⌘K / Ctrl+K to toggle visibility.
//   2. Renders the filtered command registry grouped by domain.
//   3. Folds in /api/v1/search results when the query is ≥ 3 chars
//      and no exact command id matches — so typing a driver name
//      surfaces the driver as a navigable record (Section 9).
//   4. Routes Enter to invokeCommand; supports ⌘1..⌘9 to fire the
//      Nth visible row.
//   5. Closes on Esc / handler success.
import * as React from "react";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useCommandContext } from "@/lib/commands/context";
import { commands } from "@/lib/commands/registry";
import { eligible } from "@/lib/commands/filter";
import { invokeCommand } from "@/lib/commands/handler";
import { matchEvent, isTypingInInput } from "@/lib/commands/hotkey";
import { HotkeyHint } from "@/components/command-bar/hotkey-hint";
import { ConfirmDialog } from "@/components/command-bar/confirm-dialog";
import { useSearch } from "@/lib/hooks/use-search";
import type { Command as CommandT, CommandGroup as Group } from "@/lib/commands/types";

const groupOrder: Group[] = [
  "navigation",
  "load",
  "driver",
  "customer",
  "fulfillment",
  "pricing",
  "payouts",
  "billing",
  "admin",
];

const groupLabels: Record<Group, string> = {
  navigation: "Navigation",
  load: "Load",
  driver: "Driver",
  customer: "Customer",
  fulfillment: "Fulfillment",
  pricing: "Pricing",
  payouts: "Payouts",
  billing: "Billing",
  admin: "Admin",
};

export function CommandBar() {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [pendingConfirm, setPendingConfirm] = React.useState<CommandT | null>(
    null,
  );
  const ctx = useCommandContext();

  // ⌘K global toggle. The "k" condition matches Linear/Raycast.
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (matchEvent({ mod: ["mod"], key: "k" }, e)) {
        if (isTypingInInput(e.target) && !open) {
          // Don't steal ⌘K from a text input that maps it elsewhere.
          // The palette itself has its own input — so when open we
          // always intercept.
        }
        e.preventDefault();
        setOpen((prev) => !prev);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // When the palette closes, drop the query so the next open is a
  // clean slate. Confirm modals persist their own state and are
  // closed via their dismiss path.
  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // Fold search records in when the query is long enough. We only
  // call the hook when the dialog is open to avoid background
  // requests on every page.
  const searchActive = open && query.trim().length >= 3;
  const { results } = useSearch(searchActive ? query : "");

  const eligibles = React.useMemo(
    () => commands.filter((c) => eligible(c, ctx)),
    [ctx],
  );

  function handleInvoke(cmd: CommandT) {
    if (cmd.confirmation) {
      setPendingConfirm(cmd);
      return;
    }
    setOpen(false);
    void invokeCommand(cmd, ctx, "palette");
  }

  function confirmAndRun() {
    if (!pendingConfirm) return;
    const cmd = pendingConfirm;
    setPendingConfirm(null);
    setOpen(false);
    void invokeCommand(cmd, ctx, "palette");
  }

  // groupEligibles produces the section list rendered by cmdk.
  // cmdk handles fuzzy match internally; we just feed groups.
  const grouped = React.useMemo(() => {
    const m = new Map<Group, CommandT[]>();
    for (const cmd of eligibles) {
      const arr = m.get(cmd.group) ?? [];
      arr.push(cmd);
      m.set(cmd.group, arr);
    }
    return m;
  }, [eligibles]);

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <Command label="Command palette">
          <CommandInput
            placeholder="Type a command, search, or jump…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>No matches.</CommandEmpty>

            {groupOrder.map((group) => {
              const list = grouped.get(group);
              if (!list || list.length === 0) return null;
              return (
                <CommandGroup key={group} heading={groupLabels[group]}>
                  {list.map((cmd) => (
                    <CommandItem
                      key={cmd.id}
                      value={`${cmd.label} ${(cmd.keywords ?? []).join(" ")}`}
                      onSelect={() => handleInvoke(cmd)}
                    >
                      <div className="flex flex-col">
                        <span>{cmd.label}</span>
                        {cmd.description ? (
                          <span className="text-xs text-muted-foreground">
                            {cmd.description}
                          </span>
                        ) : null}
                      </div>
                      {cmd.hotkey ? <HotkeyHint hotkey={cmd.hotkey} /> : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}

            {searchActive ? (
              <>
                <CommandSeparator />
                <CommandGroup heading="Records">
                  {results.loads.map((l) => (
                    <CommandItem
                      key={`load-${l.id}`}
                      value={`load ${l.id} ${l.delivery_method}`}
                      onSelect={() => {
                        setOpen(false);
                        ctx.router.push(`/loads?id=${l.id}`);
                      }}
                    >
                      <span className="font-mono text-xs">
                        Load {l.id.slice(0, 8)}…
                      </span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {l.delivery_method} · {l.status}
                      </span>
                    </CommandItem>
                  ))}
                  {results.drivers.map((d) => (
                    <CommandItem
                      key={`driver-${d.id}`}
                      value={`driver ${d.name} ${d.email ?? ""} ${d.phone ?? ""}`}
                      onSelect={() => {
                        setOpen(false);
                        ctx.router.push(`/fleet/drivers?id=${d.id}`);
                      }}
                    >
                      <span>Driver: {d.name}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {d.status}
                      </span>
                    </CommandItem>
                  ))}
                  {results.customers.map((c) => (
                    <CommandItem
                      key={`cust-${c.id}`}
                      value={`customer ${c.name} ${c.external_id ?? ""}`}
                      onSelect={() => {
                        setOpen(false);
                        ctx.router.push(`/customers?id=${c.id}`);
                      }}
                    >
                      <span>Customer: {c.name}</span>
                    </CommandItem>
                  ))}
                  {results.orders.map((o) => (
                    <CommandItem
                      key={`order-${o.id}`}
                      value={`order ${o.id} ${o.external_ref ?? ""}`}
                      onSelect={() => {
                        setOpen(false);
                        ctx.router.push(`/orders?id=${o.id}`);
                      }}
                    >
                      <span className="font-mono text-xs">
                        Order {o.external_ref ?? `${o.id.slice(0, 8)}…`}
                      </span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {o.status}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </CommandDialog>

      {pendingConfirm && pendingConfirm.confirmation ? (
        <ConfirmDialog
          open
          config={pendingConfirm.confirmation}
          ctx={ctx}
          onConfirm={confirmAndRun}
          onClose={() => setPendingConfirm(null)}
        />
      ) : null}
    </>
  );
}
